import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackContext } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { StripeLogger } from '../../utils/logger';

export const useNotifyStripeError = () => {
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const userFacingErrorMessage = formatMessage(messages.error);

  return useCallback(
    (stripeErrorMessage = userFacingErrorMessage, options: Options = {}) => {
      const { canShowErrorMessage = false } = options;

      const sanitizedMessage = hasStripeKeys(stripeErrorMessage)
        ? userFacingErrorMessage
        : stripeErrorMessage;

      const message = canShowErrorMessage ? sanitizedMessage : userFacingErrorMessage;

      // Logging sanitized error to console and datadog.
      StripeLogger.logGenericError(new Error(sanitizedMessage));

      // Logging system error to Mixpanel.
      track('checkout.stripe-usage', {
        event: 'stripe_failure',
        error: stripeErrorMessage,
        message,
      });

      // Showing banner with user facing error.
      addNoticeBanner({ text: message, palette: 'caution' });
    },
    [userFacingErrorMessage, track, addNoticeBanner],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const hasStripeKeys = (stripeErrorMessage: string) => {
  return STRIPE_KEYS_TO_REDACT.some((key) => stripeErrorMessage.includes(key));
};

// ─── Constants ───────────────────────────────────────────────────────────────

const STRIPE_KEYS_TO_REDACT = ['cus_', 'ek_live_', 'cuss_secret_', 'pk_live_'];

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  error: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Stripe Payment | Unknown Error',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Options = {
  canShowErrorMessage?: boolean;
};
